import { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Container,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const navItems = [
  { label: 'Inicio', href: '#inicio' },
  { label: 'Solución', href: '#solucion' },
  { label: 'Servicios', href: '#servicios' },
  { label: 'Plataforma', href: '#aplicativo' },
  { label: 'Clientes', href: '#clientes' },
  { label: 'Recursos', href: '#recursos' },
  { label: 'Contacto', href: '#contacto' },
];

const PublicNavbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleScroll = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileOpen(false);
  };

  const drawer = (
    <Box sx={{ width: 280, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5, borderBottom: '1px solid rgba(29,31,32,0.16)' }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: '#1d2d3d', letterSpacing: '-0.02em' }}>
          simplia
        </Typography>
        <IconButton onClick={() => setMobileOpen(false)} size="small">
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
      <List sx={{ px: 1.5, py: 2, flexGrow: 1 }}>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding sx={{ mb: 0.25 }}>
            <ListItemButton
              onClick={() => handleScroll(item.href)}
              sx={{ borderRadius: 0, py: 1 }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500, color: '#5d5d60' }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{ p: 2, borderTop: '1px solid rgba(29,31,32,0.16)' }}>
        <Button
          variant="contained"
          fullWidth
          onClick={() => navigate(isAuthenticated ? '/dashboard' : '/auth/login')}
          sx={{
            py: 1.25,
            bgcolor: '#5980a6',
            fontWeight: 700,
            borderRadius: 0,
            boxShadow: 'none',
            '&:hover': { bgcolor: '#416180', boxShadow: 'none' },
          }}
        >
          {isAuthenticated ? 'Ir al Dashboard' : 'Iniciar sesión'}
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: scrolled ? 'rgba(242,242,243,0.95)' : '#f2f2f3',
          backdropFilter: scrolled ? 'blur(12px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(29,31,32,0.16)' : '1px solid transparent',
          transition: 'all 0.25s ease',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ height: 64 }}>
            {/* Logo */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mr: 4 }}
              onClick={() => handleScroll('#inicio')}
            >
              <Box
                component="img"
                src="/Horizontal_Logo.jpeg"
                alt="Simplia"
                sx={{ height: 26, objectFit: 'contain' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#1d2d3d', letterSpacing: '-0.02em', lineHeight: 1 }}>
                simplia
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {isMobile ? (
              <IconButton onClick={() => setMobileOpen(true)} sx={{ color: '#5d5d60' }}>
                <MenuIcon />
              </IconButton>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {navItems.map((item) => (
                  <Button
                    key={item.label}
                    onClick={() => handleScroll(item.href)}
                    sx={{
                      color: '#5d5d60',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      px: 1.25,
                      '&:hover': { color: '#5980a6', bgcolor: 'rgba(89,128,166,0.06)' },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
                <Button
                  variant="contained"
                  onClick={() => navigate(isAuthenticated ? '/dashboard' : '/auth/login')}
                  sx={{
                    ml: 2,
                    bgcolor: '#5980a6',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    px: 2.5,
                    py: 0.875,
                    borderRadius: 0,
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: '#416180',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {isAuthenticated ? 'Dashboard' : 'Iniciar sesión'}
                </Button>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{ sx: { borderRadius: 0 } }}
      >
        {drawer}
      </Drawer>

      <Toolbar sx={{ height: 64 }} />
    </>
  );
};

export default PublicNavbar;
